// iter-121.118 — EA Action Drawers
//
// Mirror of OaActionDrawer but all endpoints are tenant-scoped. Each
// drawer surfaces row-level detail + inline action buttons.
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw, Mail, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useEaScope } from "@/lib/ea-scope";
import { roleLabel } from "@/lib/terminology";

function timeFromNow(iso) {
    if (!iso) return "—";
    const diff = (new Date(iso).getTime() - Date.now()) / 1000;
    const sign = diff < 0 ? "-" : "";
    const abs = Math.abs(diff);
    if (abs < 3600) return `${sign}${Math.round(abs / 60)}m`;
    if (abs < 86400) return `${sign}${Math.round(abs / 3600)}h`;
    return `${sign}${Math.round(abs / 86400)}d`;
}

function DrawerTable({ columns, rows, emptyMsg, testidPrefix }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid={`${testidPrefix}-empty`}>
                {emptyMsg || "No rows."}
            </div>
        );
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-[12px]" data-testid={`${testidPrefix}-table`}>
                <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        {columns.map((c) => (
                            <th key={c.key} className={`text-left px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.connector_id || r.share_id || r.session_id || r.load_id || r.invitation_id || i}
                            className="border-t border-border"
                            data-testid={`${testidPrefix}-row-${i}`}>
                            {columns.map((c) => (
                                <td key={c.key}
                                    className={`px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function useEaDrawerData(key) {
    const [rows, setRows] = useState(null);
    const [reloadAt, setReloadAt] = useState(0);
    const { queryString } = useEaScope();
    useEffect(() => {
        setRows(null);
        api.get(`/home/ea/actions/${key}?${queryString || ""}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [key, queryString, reloadAt]);
    const reload = useCallback(() => setReloadAt(Date.now()), []);
    return [rows, reload];
}

function DrawerHeader({ onReload, onNavigateAll, label }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                {label || "Detail rows"}
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={onReload}
                        className="h-7 px-2" data-testid="ea-drawer-reload">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
                </Button>
                {onNavigateAll && (
                    <Button size="sm" variant="outline" onClick={onNavigateAll}
                            className="h-7 px-2" data-testid="ea-drawer-open-all">
                        Open full view <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </Button>
                )}
            </div>
        </div>
    );
}

// ── Schema mappings ────────────────────────────────────────────────────
function SchemaMappingDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("schema-mapping-pending");
    const columns = [
        { key: "connector_name", label: "Connector",
          render: (r) => <span className="font-medium">{r.connector_name}</span> },
        { key: "source_type", label: "Type",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.source_type}</span> },
        { key: "reason", label: "Reason",
          render: (r) => <span className="text-[11px] text-muted-foreground">{r.reason}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?connector=${r.connector_id}&action=map`)}
                      data-testid={`ea-map-${r.connector_id}`}>
                  Map fields
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/data?tab=connectors")}
                label="Connectors in your entity awaiting field mapping" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading connectors…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No connectors need field mapping."
                               testidPrefix="ea-schema-mapping" />}
        </>
    );
}

// ── Workspace shares received (personal) ───────────────────────────────
function WorkspaceSharesDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("workspace-shares-received");
    const accept = async (shareId, wsName) => {
        try {
            await api.post(`/home/ea/actions/accept-share/${shareId}`);
            toast.success(`Accepted "${wsName}"`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to accept share");
        }
    };
    const decline = async (shareId, wsName) => {
        if (!window.confirm(`Decline share for "${wsName}"?`)) return;
        try {
            await api.post(`/home/ea/actions/decline-share/${shareId}`);
            toast.success("Share declined");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to decline share");
        }
    };
    const columns = [
        { key: "workspace_name", label: "Workspace",
          render: (r) => <span className="font-medium">{r.workspace_name}</span> },
        { key: "from_tenant_name", label: "From entity",
          render: (r) => <span className="text-[11px] text-muted-foreground">{r.from_tenant_name}</span> },
        { key: "access", label: "Access",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider">{r.access === "A" ? "Admin" : r.access === "W" ? "Write" : "Read"}</span> },
        { key: "granted_at", label: "Sent", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.granted_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => accept(r.share_id, r.workspace_name)}
                          data-testid={`ea-share-accept-${r.share_id}`}>
                      <Check className="h-3 w-3 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive"
                          onClick={() => decline(r.share_id, r.workspace_name)}
                          data-testid={`ea-share-decline-${r.share_id}`}>
                      <X className="h-3 w-3 mr-1" /> Decline
                  </Button>
              </div>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/workspace")}
                label="Pending workspace invitations addressed to you" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading invitations…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No pending workspace invitations."
                               testidPrefix="ea-workspace-shares" />}
        </>
    );
}

// ── Unread AI replies (personal) ───────────────────────────────────────
function UnreadAiRepliesDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("unread-ai-replies");
    const markRead = async (sessionId, title) => {
        try {
            await api.post(`/home/ea/actions/mark-chat-read/${sessionId}`);
            toast.success(`Marked "${title}" as read`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to mark as read");
        }
    };
    const columns = [
        { key: "title", label: "Conversation",
          render: (r) => <span className="font-medium">{r.title}</span> },
        { key: "updated_at", label: "Last reply", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.updated_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => onNavigate(`/sense-ai?session=${r.session_id}`)}
                          data-testid={`ea-chat-resume-${r.session_id}`}>
                      Resume
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-muted-foreground"
                          onClick={() => markRead(r.session_id, r.title)}
                          data-testid={`ea-chat-mark-read-${r.session_id}`}>
                      Mark read
                  </Button>
              </div>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/sense-ai")}
                label="Chats with replies newer than your last visit" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading chats…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No unread AI replies."
                               testidPrefix="ea-unread-replies" />}
        </>
    );
}

// ── Connectors failing ─────────────────────────────────────────────────
function ConnectorsFailingDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("connectors-failing");
    const columns = [
        { key: "connector_name", label: "Connector",
          render: (r) => <span className="font-medium">{r.connector_name}</span> },
        { key: "source_type", label: "Type",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.source_type}</span> },
        { key: "last_sync_at", label: "Failed", align: "right",
          render: (r) => <span className="text-destructive">{timeFromNow(r.last_sync_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?connector=${r.connector_id}`)}
                      data-testid={`ea-conn-diagnose-${r.connector_id}`}>
                  Diagnose
              </Button>
          ),
        },
    ];
    const drilldown = rows && rows[0] ? (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
            Latest error: <span className="text-foreground">{rows[0].last_error}</span>
        </div>
    ) : null;
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/data?tab=connectors")}
                label="Connectors that failed in the last 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading connectors…</div>
                : (
                    <>
                        <DrawerTable columns={columns} rows={rows}
                                     emptyMsg="No connector failures in the last 24h."
                                     testidPrefix="ea-connectors-failing" />
                        {drilldown}
                    </>
                )}
        </>
    );
}

// ── Stalled loads ──────────────────────────────────────────────────────
function StalledLoadsDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("stalled-loads");
    const columns = [
        { key: "dataset_name", label: "Dataset",
          render: (r) => <span className="font-medium">{r.dataset_name}</span> },
        { key: "source_name", label: "Source",
          render: (r) => <span className="text-[11px] text-muted-foreground">{r.source_name}</span> },
        { key: "status", label: "Status",
          render: (r) => {
              const tone = r.status === "failed" ? "text-destructive" : "text-amber-600 dark:text-amber-400";
              return <span className={`text-[10px] font-mono uppercase tracking-wider ${tone}`}>{r.status}</span>;
          },
        },
        { key: "loaded_at", label: "When", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.loaded_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?dataset=${r.dataset_id}`)}
                      data-testid={`ea-load-inspect-${r.load_id}`}>
                  Inspect
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/data")}
                label="Dataset loads failed or pending > 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading loads…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No stalled loads."
                               testidPrefix="ea-stalled-loads" />}
        </>
    );
}

// ── Pending team invites ───────────────────────────────────────────────
function PendingInvitesDrawer({ onNavigate }) {
    const [rows, reload] = useEaDrawerData("pending-team-invites");
    const resend = async (invitationId, email) => {
        try {
            await api.post(`/auth/invite/${invitationId}/resend`);
            toast.success(`Invitation resent to ${email}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to resend invite");
        }
    };
    const cancel = async (invitationId, email) => {
        if (!window.confirm(`Cancel pending invite to ${email}?`)) return;
        try {
            await api.delete(`/auth/invite/${invitationId}`);
            toast.success(`Invitation cancelled for ${email}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to cancel invite");
        }
    };
    const columns = [
        { key: "email", label: "Email",
          render: (r) => <span className="font-medium">{r.email}</span> },
        { key: "role", label: "Role",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{roleLabel(r.role)}</span> },
        { key: "created_at", label: "Sent", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.created_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => resend(r.invitation_id, r.email)}
                          data-testid={`ea-invite-resend-${r.invitation_id}`}>
                      <Mail className="h-3 w-3 mr-1" /> Resend
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive"
                          onClick={() => cancel(r.invitation_id, r.email)}
                          data-testid={`ea-invite-cancel-${r.invitation_id}`}>
                      Cancel
                  </Button>
              </div>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/team")}
                label="Outstanding team-member invitations" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading invites…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No pending invites."
                               testidPrefix="ea-pending-invites" />}
        </>
    );
}

const RENDERERS = {
    schema_mapping_pending:    SchemaMappingDrawer,
    workspace_shares_received: WorkspaceSharesDrawer,
    unread_ai_replies:         UnreadAiRepliesDrawer,
    connectors_failing:        ConnectorsFailingDrawer,
    stalled_loads:             StalledLoadsDrawer,
    pending_team_invites:      PendingInvitesDrawer,
};

export default function EaActionDrawer({ item, onClose, onNavigate }) {
    const Renderer = RENDERERS[item.key];
    if (!Renderer) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid="ea-action-drawer-unknown">
                No detail view available for `{item.key}`.
            </div>
        );
    }
    return <Renderer onClose={onClose} onNavigate={onNavigate} />;
}
